import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, Store, Smartphone, ImagePlus, Trash2, Loader2, HelpCircle, LogIn, LogOut } from "lucide-react";
import { getShop, saveShop, shopLogoUrl, uploadShopLogo, deleteShopLogo } from "@/lib/api";
import LinkWhatsApp from "@/components/LinkWhatsApp";
import LoginModal from "@/components/LoginModal";
import SetupGuide from "@/components/SetupGuide";

function LogoSection({ hasLogo, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ver, setVer] = useState(0);

  const pick = () => fileRef.current && fileRef.current.click();
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    setBusy(true);
    try {
      await uploadShopLogo(f);
      setVer((v) => v + 1);
      onChanged(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Logo upload nahi hua");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await deleteShopLogo();
      onChanged(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-white/70">
      <p className="text-[12px] font-bold text-gray-700 mb-0.5">Dukaan ka logo</p>
      <p className="text-[10px] text-gray-400 mb-2">PDF bill pe print hoga — professional look ke liye (PNG/JPG, max 2MB)</p>
      <div className="flex items-center gap-3">
        {hasLogo && (
          <img data-testid="shop-logo-preview" src={`${shopLogoUrl()}?v=${ver}`} alt="Shop logo"
            className="w-12 h-12 rounded-lg object-contain border border-gray-200 bg-white" />
        )}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="shop-logo-input" />
        <button data-testid="shop-logo-upload-btn" onClick={pick} disabled={busy}
          className="inline-flex items-center gap-1.5 border border-[#006D5B]/40 text-[#006D5B] text-[11px] font-bold px-3 py-1.5 rounded-full hover:bg-[#006D5B]/5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
          {hasLogo ? "Logo badlo" : "Logo upload karo"}
        </button>
        {hasLogo && (
          <button data-testid="shop-logo-remove-btn" onClick={remove} disabled={busy} title="Logo hatao"
            className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
        )}
      </div>
      {err && <p data-testid="shop-logo-error" className="text-[10px] text-red-500 mt-1.5">{String(err)}</p>}
    </div>
  );
}

const FIELDS = [
  { key: "store_name", label: "Dukaan ka naam", placeholder: "Sharma Kirana Store" },
  { key: "gstin", label: "GSTIN (optional)", placeholder: "22AAAAA0000A1Z5" },
  { key: "address", label: "Address (optional)", placeholder: "Main Bazaar, Jaipur" },
  { key: "invoice_prefix", label: "Bill prefix", placeholder: "INV-" },
  { key: "invoice_start", label: "Bill start number", placeholder: "1", type: "number" },
];

const DIGEST_TIMES = [];
for (let h = 17; h <= 22; h++) for (const m of ["00", "30"]) DIGEST_TIMES.push(`${String(h).padStart(2, "0")}:${m}`);
DIGEST_TIMES.push("23:00");
const timeLabel = (t) => {
  const [h, m] = t.split(":").map(Number);
  const h12 = h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} PM`;
};

function LinkPhoneSection({ linkedPhone, onLinked }) {
  return (
    <>
      <p className="text-[11px] text-gray-500 mb-2">
        Apna WhatsApp number link karo — Pro subscription website aur WhatsApp dono pe chalegi, aur kisi bhi device pe account wapas milega.
      </p>
      <LinkWhatsApp linkedPhone={linkedPhone} onLinked={onLinked} />
    </>
  );
}

export default function ShopSettings({ onClose, onLinked }) {
  const [form, setForm] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getShop()
      .then(({ data }) => setForm(data))
      .catch((e) => {
        console.error("shop load failed", e);
        setForm({});
      });
  }, []);

  const gstOn = form ? (form.gst_enabled ?? Boolean(form.gstin)) : false;

  const save = async () => {
    setSaving(true);
    try {
      await saveShop({
        store_name: form.store_name || null,
        gstin: form.gstin || null,
        address: form.address || null,
        invoice_prefix: form.invoice_prefix || null,
        invoice_start: form.invoice_start ? Number(form.invoice_start) : null,
        gst_enabled: gstOn,
        gst_rate: form.gst_rate != null && form.gst_rate !== "" ? Number(form.gst_rate) : 18,
        digest_time: form.digest_time || "20:00",
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error("shop save failed", e);
    }
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      data-testid="shop-settings-panel"
      className="absolute inset-0 z-30 bg-[#FDFBF7] flex flex-col"
    >
      <div className="bg-[#006D5B] text-white px-4 py-3 flex items-center gap-3">
        <Store size={18} />
        <p className="font-semibold text-sm flex-1">Dukaan Settings</p>
        <button data-testid="shop-settings-close-btn" onClick={onClose}
          className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors duration-150">
          <X size={17} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto chat-scroll p-4 space-y-5">
        {form === null ? (
          <p className="text-sm text-gray-400 text-center mt-10">Loading...</p>
        ) : (
          <>
            <button
              data-testid="setup-guide-toggle"
              onClick={() => setShowGuide((v) => !v)}
              className="w-full flex items-center gap-2 bg-[#006D5B]/8 border border-[#006D5B]/20 text-[#006D5B] text-[12px] font-bold px-3 py-2 rounded-xl hover:bg-[#006D5B]/12 transition-colors duration-150"
            >
              <HelpCircle size={14} /> {showGuide ? "Setup guide band karo" : "Naye ho? Setup guide dekho (2 min)"}
            </button>
            {showGuide && <SetupGuide />}
            <div className="space-y-3">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">Bill pe dikhega</p>
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-[11px] font-semibold text-gray-600 block mb-1">{f.label}</label>
                  <input
                    data-testid={`shop-${f.key}-input`}
                    type={f.type || "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full border border-gray-200 rounded-full px-3.5 py-2 text-[12px] outline-none focus:ring-2 focus:ring-[#006D5B]/40"
                  />
                </div>
              ))}
              <LogoSection hasLogo={Boolean(form.has_logo)} onChanged={(v) => setForm({ ...form, has_logo: v })} />
              <div className="border border-gray-200 rounded-xl p-3 bg-white/70 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[12px] font-bold text-gray-700">GST bills pe dikhao</p>
                    <p className="text-[10px] text-gray-400">GSTIN bharne pe apne aap on ho jata hai</p>
                  </div>
                  <button
                    data-testid="shop-gst-toggle"
                    role="switch"
                    aria-checked={gstOn}
                    onClick={() => setForm({ ...form, gst_enabled: !gstOn })}
                    className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${gstOn ? "bg-[#006D5B]" : "bg-gray-300"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${gstOn ? "translate-x-5" : ""}`} />
                  </button>
                </div>
                {gstOn && (
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-semibold text-gray-600">Default GST %</label>
                    <select
                      data-testid="shop-gst-rate-select"
                      value={form.gst_rate ?? 18}
                      onChange={(e) => setForm({ ...form, gst_rate: Number(e.target.value) })}
                      className="border border-gray-200 rounded-full px-3 py-1.5 text-[12px] font-mono outline-none focus:ring-2 focus:ring-[#006D5B]/40 bg-white"
                    >
                      {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                    </select>
                    <span className="text-[10px] text-gray-400">Chat mein alag % bologe to wahi lagega</span>
                  </div>
                )}
                {!gstOn && (
                  <p data-testid="shop-gst-off-note" className="text-[10px] text-gray-400">
                    Bills pe GST line nahi aayegi — sirf items aur total.
                  </p>
                )}
              </div>
              <div className="border border-gray-200 rounded-xl p-3 bg-white/70">
                <p className="text-[12px] font-bold text-gray-700">Dukaan band hone ka time</p>
                <p className="text-[10px] text-gray-400 mb-2">Roz is time WhatsApp pe din ka digest aayega — sale, udhaar, stock report</p>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="shop-digest-time-select"
                    value={form.digest_time ?? "20:00"}
                    onChange={(e) => setForm({ ...form, digest_time: e.target.value })}
                    className="border border-gray-200 rounded-full px-3 py-1.5 text-[12px] font-mono outline-none focus:ring-2 focus:ring-[#006D5B]/40 bg-white"
                  >
                    {DIGEST_TIMES.map((t) => <option key={t} value={t}>{timeLabel(t)}</option>)}
                  </select>
                  <span className="text-[10px] text-gray-400">WhatsApp pe *digest time 9* likh kar bhi badal sakte ho</span>
                </div>
              </div>
              <button data-testid="shop-save-btn" onClick={save} disabled={saving}
                className="w-full bg-[#006D5B] text-white text-sm font-bold py-2.5 rounded-full shadow-md disabled:opacity-50">
                {saved ? "Save ho gaya ✓" : saving ? "Saving..." : "Save karo"}
              </button>
              <p className="text-[10px] text-gray-400">Naye bills pe naam, GSTIN aur bill number series apply hogi.</p>
            </div>
            <div className="border-t border-dashed border-gray-200 pt-4 space-y-2">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <Smartphone size={12} /> WhatsApp account link
              </p>
              <LinkPhoneSection linkedPhone={form.linked_phone} onLinked={onLinked} />
            </div>
            <div className="border-t border-dashed border-gray-200 pt-4 space-y-2 pb-2">
              <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                <LogIn size={12} /> Account
              </p>
              {!form.linked_phone && (
                <button
                  data-testid="open-login-btn"
                  onClick={() => setShowLogin(true)}
                  className="w-full flex items-center justify-center gap-2 border border-[#006D5B]/40 text-[#006D5B] text-[12px] font-bold px-3 py-2.5 rounded-full hover:bg-[#006D5B]/5 transition-colors duration-150"
                >
                  <LogIn size={13} /> Pehle se account hai? Mobile number se login karo
                </button>
              )}
              {form.linked_phone && (
                <>
                  <p className="text-[11px] text-gray-500">Logged in: <span className="font-mono font-bold">+{form.linked_phone}</span></p>
                  <button
                    data-testid="logout-btn"
                    onClick={() => { if (window.confirm("Pakka logout karna hai? Wapas login karke account dubara mil jayega.")) { localStorage.removeItem("sahayak_session"); window.location.reload(); } }}
                    className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-500 text-[12px] font-bold px-3 py-2.5 rounded-full hover:bg-red-50 transition-colors duration-150"
                  >
                    <LogOut size={13} /> Logout (is device se)
                  </button>
                  <p className="text-[10px] text-gray-400">Logout ke baad naya khaali session milega — wapas login karke apna account dubara pa sakte ho.</p>
                </>
              )}
            </div>
            {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
          </>
        )}
      </div>
    </motion.div>
  );
}
